import { useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import moment from 'moment';
import {
  getHudVouchers,
  getHudVoucherSetup,
  startHudVoucherDraftAutomation,
  updateHudVoucherBeginDate,
  undoHudVoucherSetup,
  downloadMat30File,
} from '../../services/hudVouchersService';
import { toastr } from 'react-redux-toastr';
import useIntl from '../../hooks/useIntl';
import messages from './messages';
import {
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { HEADERS, NAME } from './HUDVouchersTable/constants';
import { useRows } from './HUDVouchersTable/hooks';
import useUniqueTableName from '../../hooks/useUniqueTableName';
import useActionsFirstColumnOrder from '../../hooks/useActionsFirstColumnOrder';
import HudVoucherService from '../../services/hudVoucherService';

// Map frontend field names to backend field names
const mapSortFieldToBackend = (field) => {
  // Most fields match, but voucherYearMonth is already correct
  return field;
};

// Map filter state to backend params
const mapFilterState = (filterState, params) => {
  if (!filterState || Object.keys(filterState).length === 0) {
    return;
  }

  Object.entries(filterState).forEach(([key, value]) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return;
    }

    // Backend accepts arrays for some filters
    if (key === 'submissionStatus') {
      params.submissionStatus = Array.isArray(value) ? value : [value];
    } else if (key === 'subsidyType') {
      params.subsidyType = Array.isArray(value) ? value : [value];
    }
  });
};

// Map search state to backend params
// tracsVoucherId maps to tracsVoucherId filter parameter
// tracsMessage maps to searchQuery (which searches notes field in backend)
const mapSearchState = (searchState, params) => {
  if (!searchState || Object.keys(searchState).length === 0) {
    return;
  }

  // Map tracsVoucherId to filter parameter
  if (searchState.tracsVoucherId) {
    params.tracsVoucherId = searchState.tracsVoucherId;
  }

  if (searchState.subsidyUnits) {
    params.subsidyUnits = searchState.subsidyUnits;
  }

  if (searchState.tracsMessage) {
    // Only include tracsMessage in searchQuery (maps to notes field in backend)
    params.searchQuery = searchState.tracsMessage;
  }
};

// Map voucherYearMonth date field to backend params
const mapVoucherYearMonthDate = (value, params) => {
  const hasFromDate =
    value.fromDate &&
    moment.isMoment(value.fromDate) &&
    value.fromDate.isValid();
  const hasToDate =
    value.toDate && moment.isMoment(value.toDate) && value.toDate.isValid();

  if (hasFromDate && hasToDate) {
    // Both dates set - use voucherYearMonth range filtering
    params.voucherYearMonthFrom = value.fromDate.format('YYYY-MM');
    params.voucherYearMonthTo = value.toDate.format('YYYY-MM');
  } else if (hasFromDate) {
    // Only fromDate set - use single voucherYearMonth filter
    params.voucherYearMonth = value.fromDate.format('YYYY-MM');
  } else if (hasToDate) {
    // Only toDate set - use single voucherYearMonth filter
    params.voucherYearMonth = value.toDate.format('YYYY-MM');
  }
};

// Map lastSubmissionDate to backend params
const mapLastSubmissionDate = (value, params) => {
  if (
    value.fromDate &&
    moment.isMoment(value.fromDate) &&
    value.fromDate.isValid()
  ) {
    params.lastSubmissionDateFrom = value.fromDate.toISOString();
  }
  if (value.toDate && moment.isMoment(value.toDate) && value.toDate.isValid()) {
    params.lastSubmissionDateTo = value.toDate.toISOString();
  }
};

// Map date state to backend params
const mapDateState = (dateState, params) => {
  if (!dateState || Object.keys(dateState).length === 0) {
    return;
  }

  Object.entries(dateState).forEach(([key, value]) => {
    if (!value) {
      return;
    }

    if (key === 'voucherYearMonth') {
      mapVoucherYearMonthDate(value, params);
    } else if (key === 'lastSubmissionDate') {
      mapLastSubmissionDate(value, params);
    }
    // hudResponseDate filters are not supported by backend yet
  });
};

// Convert filter state, search state, and date state to backend query params
const buildBackendParams = ({
  filterState,
  searchState,
  dateState,
  order,
  orderBy,
  page = 1,
  limit = 100,
}) => {
  const params = {
    page,
    limit,
    sort: mapSortFieldToBackend(orderBy || 'voucherYearMonth'),
    order: order || 'DESC',
  };

  mapFilterState(filterState, params);
  mapSearchState(searchState, params);
  mapDateState(dateState, params);

  return params;
};

export const useHudVouchersQuery = ({
  organizationId,
  propertyId,
  filterState,
  searchState,
  dateState,
  order,
  orderBy,
  page,
  limit,
}) => {
  const intl = useIntl();

  const backendParams = useMemo(
    () =>
      buildBackendParams({
        filterState,
        searchState,
        dateState,
        order,
        orderBy,
        page,
        limit,
      }),
    [filterState, searchState, dateState, order, orderBy, page, limit],
  );

  const queryKey = ['hudVouchers', organizationId, propertyId, backendParams];

  const { data, isLoading, isError, error } = useQuery(
    queryKey,
    async () => {
      const response = await getHudVouchers({
        organizationId,
        propertyId,
        params: backendParams,
      });

      return {
        results: response?.results || [],
        totalCount: response?.meta?.totalCount || response?.meta?.count || 0,
      };
    },
    {
      enabled: Boolean(organizationId && propertyId),
      refetchOnWindowFocus: false,
      onError: (e) => {
        const errorMessage =
          e?.message || e?.error || intl.formatMessage(messages.loadError);
        toastr.error('Error', errorMessage);
      },
    },
  );

  return {
    vouchers: data?.results || [],
    totalCount: data?.totalCount || 0,
    isLoading,
    isError,
    error,
  };
};

/**
 * Whether Fortress HUD Vouchering is set up for the property and from which month.
 * Property level and slow moving, so it is cached for the session rather than refetched per row.
 *
 * `isUnresolved` covers the failed request as well as the in-flight one. A failure leaves the same
 * defaults an unconfigured property has, so callers that only watch `isLoading` would judge a
 * vouchering property by the allTRACS rule and state a reason that is not true. Unresolved is the
 * honest answer in both cases: hold the action, say nothing about why.
 */
export const useHudVoucherSetupQuery = ({
  organizationId,
  propertyId,
  enabled = true,
}) => {
  const { data, isLoading, isError } = useQuery(
    ['hudVoucherSetup', organizationId, propertyId],
    () => getHudVoucherSetup({ organizationId, propertyId }),
    {
      enabled: Boolean(enabled && organizationId && propertyId),
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  );

  return {
    isConfigured: data?.isConfigured ?? false,
    voucherSetupYearMonth: data?.voucherSetupYearMonth ?? null,
    isLoading,
    isUnresolved: isLoading || isError,
  };
};

export const useStartHudVoucherDraftAutomationMutation = ({
  organizationId,
  propertyId,
  onSuccess,
  onError,
}) => {
  const queryClient = useQueryClient();
  const intl = useIntl();

  return useMutation(
    async ({ beginDate }) => {
      return startHudVoucherDraftAutomation({
        organizationId,
        propertyId,
        beginDate,
      });
    },
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries([
          'hudVouchers',
          organizationId,
          propertyId,
        ]);
        const successMessage =
          res?.message || intl.formatMessage(messages.setupSuccess);
        toastr.success('Success', successMessage);
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      },
      onError: (e) => {
        if (typeof onError === 'function') {
          onError(e);
        } else {
          const errorMessage =
            e?.message ||
            e?.error ||
            'Failed to start automation. Please try again.';
          toastr.error('Error', errorMessage);
        }
      },
    },
  );
};

export const useUpdateHudVoucherBeginDateMutation = ({
  organizationId,
  propertyId,
  onSuccess,
  onError,
}) => {
  const queryClient = useQueryClient();
  const intl = useIntl();

  return useMutation(
    async ({ beginDate }) => {
      return updateHudVoucherBeginDate({
        organizationId,
        propertyId,
        beginDate,
      });
    },
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries([
          'hudVouchers',
          organizationId,
          propertyId,
        ]);
        const successMessage =
          res?.message || intl.formatMessage(messages.updateSuccess);
        toastr.success('Success', successMessage);
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      },
      onError: (e) => {
        if (typeof onError === 'function') {
          onError(e);
        } else {
          const errorMessage =
            e?.message ||
            e?.error ||
            'Failed to update begin date. Please try again.';
          toastr.error('Error', errorMessage);
        }
      },
    },
  );
};

// Build filter options from voucher data
const useBuildFilterOptions = ({ vouchers }) => {
  return useMemo(() => {
    const options = {
      submissionStatus: [],
      subsidyType: [],
    };

    if (vouchers) {
      vouchers.forEach((voucher) => {
        if (voucher.submissionStatus) {
          options.submissionStatus.push(voucher.submissionStatus);
        }
        if (voucher.subsidyType) {
          options.subsidyType.push(voucher.subsidyType);
        }
      });
    }

    // Remove duplicates, sort, and map to label/value objects
    // Note: We preserve original case for values (don't lowercase) since backend expects exact match
    Object.keys(options).forEach((key) => {
      const counts = {};
      options[key].forEach((value) => {
        counts[value] = (counts[value] || 0) + 1;
      });
      options[key] = Array.from(new Set(options[key]))
        .sort()
        .map((value) => ({
          text: `${value} (${counts[value]})`,
          value: value, // Preserve original case for backend
        }));
    });

    return options;
  }, [vouchers]);
};

// Build headers with filter options
const useHeadersWithFilters = ({ vouchers }) => {
  const filterOptions = useBuildFilterOptions({ vouchers });

  return useMemo(() => {
    return HEADERS.map((header) => {
      if (header.id === 'submissionStatus' || header.id === 'subsidyType') {
        return {
          ...header,
          filterOptions: filterOptions[header.id] || [],
        };
      }
      return header;
    });
  }, [filterOptions]);
};

// Configure initial date state for voucherYearMonth to show month/year picker only
const initialDateState = {
  voucherYearMonth: {
    fromDate: null,
    toDate: null,
    FromDateProps: {
      views: ['year', 'month'],
      openTo: 'year',
      placeholder: 'MM/YYYY',
    },
    ToDateProps: {
      views: ['year', 'month'],
      openTo: 'year',
      placeholder: 'MM/YYYY',
    },
  },
};

export const useDownloadMat30Mutation = ({ organizationId, propertyId }) => {
  const intl = useIntl();

  return useMutation(
    async ({ voucherId }) => {
      return downloadMat30File({ organizationId, propertyId, voucherId });
    },
    {
      onMutate: () => {
        toastr.info('Pending', intl.formatMessage(messages.downloadingMat30));
      },
      onError: (error) => {
        const errorMessage =
          error?.message || intl.formatMessage(messages.downloadMat30Error);
        // ponytail: toastr.info returns no id, so this clears every info toast, not just "Pending".
        toastr.removeByType('info');
        toastr.error('Error', errorMessage);
      },
    },
  );
};

export const useManageHudVouchersTable = ({
  organizationId,
  propertyId,
  propertyName,
  canManage,
}) => {
  const name = useUniqueTableName(NAME);

  // Get filter/sort state from table manager (using base headers initially)
  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers: HEADERS,
    initialOrderBy: 'voucherYearMonth',
    initialOrder: 'DESC',
    initialDateState,
  });

  // Fetch vouchers with filter/sort params
  const { vouchers, totalCount, isLoading, isError, error } =
    useHudVouchersQuery({
      organizationId,
      propertyId,
      filterState,
      searchState,
      dateState,
      order,
      orderBy,
      page: 1,
      limit: 100,
    });

  // Build headers with filter options from current results
  const headersWithFilters = useHeadersWithFilters({ vouchers });

  // Download MAT30 mutation
  const downloadMat30Mutation = useDownloadMat30Mutation({
    organizationId,
    propertyId,
  });

  const handleDownloadMat30 = useCallback(
    (voucherId) => {
      downloadMat30Mutation.mutate({ voucherId });
    },
    [downloadMat30Mutation],
  );

  // Download HUD 52670 handler
  const handleDownloadHud52670 = useCallback(
    async (voucherId) => {
      try {
        const service = new HudVoucherService();
        const voucher = vouchers.find((v) => v.id === voucherId);
        const safeName = (propertyName || 'Property').replace(
          /[^a-zA-Z0-9._-]/g,
          '_',
        );
        const fileName = `${
          voucher?.voucherYearMonth || 'voucher'
        }_${safeName}_52670.pdf`;
        await service.downloadHud52670(
          organizationId,
          propertyId,
          voucherId,
          fileName,
        );
      } catch (error) {
        const msg =
          typeof error === 'string'
            ? error
            : error?.error ||
              error?.networkError ||
              error?.message ||
              'Failed to download HUD 52670.';
        toastr.error('Error', msg);
      }
    },
    [vouchers, organizationId, propertyId, propertyName],
  );

  // Convert vouchers to rows
  const rows = useRows(vouchers, {
    onDownloadMat30: handleDownloadMat30,
    onDownloadHud52670: handleDownloadHud52670,
    canManage,
  });

  // Column management - use headers with filters
  const { initialColumnsOrder, shouldForceOverwrite } =
    useActionsFirstColumnOrder({ name, headers: headersWithFilters });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    handleSettingsClose,
  } = useTableManageColumns({
    name,
    headers: headersWithFilters,
    firstRow: rows[0],
    initialColumnsOrder,
    forceOverwrite: shouldForceOverwrite,
  });

  return {
    allColumnsHidden,
    columnOptions,
    count: rows.length,
    totalCount,
    dateState,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSortChange,
    handleSettingsClose,
    headers: HEADERS,
    isLoading,
    isError,
    error,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    vouchers,
  };
};

export const useUndoHudVoucherSetupMutation = ({
  organizationId,
  propertyId,
  onSuccess,
  onError,
}) => {
  const queryClient = useQueryClient();
  const intl = useIntl();

  return useMutation(
    async () => {
      return undoHudVoucherSetup({
        organizationId,
        propertyId,
      });
    },
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries([
          'hudVouchers',
          organizationId,
          propertyId,
        ]);
        const successMessage =
          res?.message || intl.formatMessage(messages.undoSuccess);
        toastr.success('Success', successMessage);
        if (typeof onSuccess === 'function') {
          onSuccess();
        }
      },
      onError: (e) => {
        if (typeof onError === 'function') {
          onError(e);
        } else {
          const errorMessage =
            e?.message || e?.error || 'Failed to undo setup. Please try again.';
          toastr.error('Error', errorMessage);
        }
      },
    },
  );
};
